import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import config from '@plone/volto/registry';

/**
 * Clone a container block so the copy is fully independent of the original
 * (#1728).
 *
 * Volto's own `cloneBlocks` only descends into containers that carry
 * `blocks`/`blocks_layout` on the block itself — `hasBlocksData()` looks at
 * top-level keys only (volto/src/helpers/Blocks/Blocks.js:58). `group`,
 * `accordion` and `tabs_block` keep their children under `data`, so
 * `cloneBlocks` returned the very same object: same nested ids, same object
 * reference. Because `deleteBlock` mutates `blocks_layout.items` in place
 * (Blocks.js:157), deleting a block in the pasted copy also removed it from
 * the original.
 *
 * We register this as `cloneData` for those block types — the hook
 * `pasteBlocks`/`cloneBlocks` already prefer over their own logic — instead of
 * shadowing core.
 *
 * It deliberately does NOT delegate to `cloneBlocks`: core looks a child's
 * `cloneData` up under the `@type` of the *parent* block (cloneBlocks.ts:18),
 * so a container nested inside another container would never be reached.
 * This helper walks both container shapes itself and resolves each child by
 * its own type.
 */

type BlockData = Record<string, any>;
type ClonedBlock = [string, BlockData];

/** A `{blocks, blocks_layout}` pair — the shape Volto itself understands. */
const isContainer = (node: unknown): node is BlockData =>
  !!node &&
  typeof node === 'object' &&
  !!(node as BlockData).blocks &&
  !!(node as BlockData).blocks_layout;

/** Give every child of a container a new id, recursively. */
function reidContainer(container: BlockData): BlockData {
  const blocks: Record<string, BlockData> = {};
  const items: string[] = [];

  for (const oldId of container.blocks_layout?.items ?? []) {
    const child = container.blocks?.[oldId];
    if (!child) {
      continue;
    }
    const cloned = cloneChild(child);
    if (!cloned) {
      // Some blocks refuse to be copied — core drops them the same way.
      continue;
    }
    const [newId, newChild] = cloned;
    blocks[newId] = newChild;
    items.push(newId);
  }

  return {
    ...container,
    blocks,
    blocks_layout: { ...container.blocks_layout, items },
  };
}

function cloneChild(child: BlockData): ClonedBlock | null {
  const cloneData =
    config.blocks?.blocksConfig?.[child?.['@type'] as string]?.cloneData;
  if (cloneData) {
    const cloned = cloneData(child);
    return cloned && cloned[0] ? (cloned as ClonedBlock) : null;
  }
  return [uuid(), reidBlock(child)];
}

/** Re-id both container shapes: `blocks` on the block, or below `data`. */
function reidBlock(block: BlockData): BlockData {
  const next: BlockData = { ...block };
  if (isContainer(next.data)) {
    next.data = reidContainer(next.data);
  }
  if (isContainer(next)) {
    Object.assign(next, reidContainer(next));
  }
  return next;
}

/**
 * Deep-copy a block and give every nested block a fresh id. The deep copy
 * matters as much as the ids: core's in-place mutations must not travel back
 * to the original block.
 */
export function cloneContainerBlockData(block: BlockData): BlockData {
  return reidBlock(cloneDeep(block));
}

/** `cloneData` signature: returns the `[id, data]` pair `pasteBlocks` expects. */
export function cloneContainerBlock(block: BlockData): ClonedBlock {
  return [uuid(), cloneContainerBlockData(block)];
}
